import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Check, Circle, X, Lock } from 'lucide-react';
import apiService from '@/services/api';

interface PermissionMatrix {
  [key: string]: {
    name: string;
    description: string;
    permissions: {
      SUPER_ADMIN: 'allow' | 'view' | 'deny';
      OPERATIONS: 'allow' | 'view' | 'deny';
      FINANCE: 'allow' | 'view' | 'deny';
      COMPLIANCE: 'allow' | 'view' | 'deny';
      SUPPORT: 'allow' | 'view' | 'deny';
      CONTENT: 'allow' | 'view' | 'deny';
    };
  };
}

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState<PermissionMatrix>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const response = await apiService.request('/admin/permissions', 'GET');
        if (response.success && response.data && Object.keys(response.data).length > 0) {
          setPermissions(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  const handleSaveChanges = async () => {
    try {
      const response = await apiService.request('/admin/permissions', 'PUT', permissions);
      if (response.success) {
        alert('✓ Permissions saved successfully');
      } else {
        alert('Error saving permissions: ' + response.message);
      }
    } catch (error: any) {
      alert('Failed to save permissions: ' + error.message);
    }
  };

  const renderPermissionIcon = (status: 'allow' | 'view' | 'deny') => {
    switch (status) {
      case 'allow':
        return (
          <div className="flex justify-center">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-green-600" />
            </div>
          </div>
        );
      case 'view':
        return (
          <div className="flex justify-center">
            <Circle className="w-5 h-5 text-amber-400" fill="currentColor" />
          </div>
        );
      case 'deny':
        return (
          <div className="flex justify-center">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
              <X className="w-4 h-4 text-red-600" />
            </div>
          </div>
        );
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-3">Settings › Permissions</p>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Role Permissions</h1>
          <p className="text-sm text-gray-600 max-w-3xl">
            Define granular access levels for administrative roles. Authorization changes impact all assigned users across the Virewire ecosystem.
          </p>
        </div>

        {/* Permissions Matrix Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading permissions...</div>
          ) : Object.keys(permissions).length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-sm">No permissions available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-1/4">Permission Module</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Super Admin</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Operations</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Finance</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Compliance</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Support</th>
                    <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-700 w-12">Content</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permissions).map(([key, perm]) => (
                    <tr key={key} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900 text-sm">{perm.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{perm.description}</p>
                      </td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.SUPER_ADMIN)}</td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.OPERATIONS)}</td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.FINANCE)}</td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.COMPLIANCE)}</td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.SUPPORT)}</td>
                      <td className="px-4 py-4">{renderPermissionIcon(perm.permissions.CONTENT)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center gap-8">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-xs font-semibold text-gray-700">ALLOWED / FULL ACCESS</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-4 h-4 text-amber-400" fill="currentColor" />
              <span className="text-xs font-semibold text-gray-700">VIEW ONLY</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <X className="w-3 h-3 text-red-600" />
              </div>
              <span className="text-xs font-semibold text-gray-700">DENIED / NO ACCESS</span>
            </div>
          </div>
        </div>

        {/* Security Notice and Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Lock className="w-4 h-4 text-gray-600" />
            <p className="text-gray-700 font-medium">Super Admin authorization required to deploy changes.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors"
            >
              Discard Changes
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={loading}
              className="px-6 py-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {loading ? 'Loading...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PermissionsPage;
